import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowRight, BookOpen } from "lucide-react";

export default function Pillar() {
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let timer;
    const fetchPillar = () => {
      api.get("/pillar")
        .then((r) => { setP(r.data); setLoading(false); })
        .catch(() => { setLoading(true); timer = setTimeout(fetchPillar, 4000); });
    };
    fetchPillar();
    return () => clearTimeout(timer);
  }, []);

  // Extract H2 headings for TOC
  const toc = p?.content ? Array.from(p.content.matchAll(/^##\s+(.+)$/gm)).map((m) => m[1]) : [];
  const slugify = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

  return (
    <div data-testid="page-pillar">
      <section className="relative pt-28 pb-16 md:pt-36 md:pb-20 bg-[#0F1A14] text-white overflow-hidden">
        <img src="https://images.unsplash.com/photo-1725103895158-694482193253?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxzYXJkaW5pYSUyMGxhbmRzY2FwZSUyMGFncmljdWx0dXJlJTIwbmF0dXJlfGVufDB8fHx8MTc4MjQwNzU1OHww&ixlib=rb-4.1.0&q=85" alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F1A14]/70 to-[#0F1A14]" />
        <img src="/assets/logo-symbol.png" alt="" aria-hidden className="absolute -right-20 top-1/2 -translate-y-1/2 w-[34rem] opacity-[0.06] pointer-events-none select-none" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#A7E2C0]">Guida completa · Pillar</span>
          <h1 className="mt-5 font-display font-black tracking-tighter text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.02]">Consulenza alle Imprese Agricole in Sardegna</h1>
          <p className="mt-6 text-lg md:text-xl text-white/85 max-w-3xl leading-relaxed">L’approfondimento completo di Betelgez Srl: strategie, bandi, PAC, innovazione e sostenibilità per la crescita delle imprese agricole sarde.</p>
        </div>
      </section>

      {loading ? (
        <section className="py-32 text-center">
          <div className="inline-flex items-center gap-3 text-[#6B7280]">
            <BookOpen className="w-5 h-5 animate-pulse" />
            Stiamo generando la guida completa con AI. Riprova tra qualche istante…
          </div>
        </section>
      ) : (
        <section className="py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
            <aside className="lg:col-span-3 order-2 lg:order-1">
              <div className="lg:sticky lg:top-28 space-y-3">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#007A3D]">Indice</div>
                <ul className="space-y-2 text-sm text-[#374151]">
                  {toc.map((t) => (
                    <li key={t}>
                      <a href={`#${slugify(t)}`} className="hover:text-[#00A651] block py-1 border-l-2 border-[#E5E7EB] hover:border-[#00A651] pl-3 transition-colors">{t}</a>
                    </li>
                  ))}
                </ul>
                <Link to="/contatti" className="mt-6 inline-flex items-center gap-2 bg-[#00A651] hover:bg-[#007A3D] text-white rounded-full px-5 py-3 text-sm font-medium" data-testid="pillar-side-cta">
                  Richiedi Consulenza <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </aside>
            <article className="lg:col-span-9 order-1 lg:order-2 prose-betelgez">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h2: ({ children, ...props }) => {
                    const text = String(children);
                    return <h2 id={slugify(text)} {...props}>{children}</h2>;
                  },
                }}
              >{p.content}</ReactMarkdown>
            </article>
          </div>
        </section>
      )}

      <section className="py-20 md:py-28 bg-[#EAF8F0]">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="font-display font-black tracking-tighter text-3xl md:text-5xl text-[#1A1A1A]">Pronto a far crescere la tua impresa agricola?</h2>
          <p className="mt-5 text-lg text-[#374151]">Una conversazione iniziale gratuita per valutare opportunità e priorità.</p>
          <Link to="/contatti" data-testid="pillar-final-cta" className="mt-8 inline-flex items-center gap-2 bg-[#00A651] hover:bg-[#007A3D] text-white rounded-full px-7 py-4 font-medium">
            Prenota una Consulenza <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
